import type { MarketplaceEntry } from "../types/index";
import type { SupportedClient } from "@skills-marketplace/contracts/contract-2-plugin-store";
import { CLIENT_CONFIG } from "./ClientBadge";
import { OfficialCheck } from "./OfficialBadge";
import NewBadge from "./NewBadge";
import UsefulnessBadge from "./UsefulnessBadge";
import SharedUsageNote from "./SharedUsageNote";
import UsersIcon from "../icons/UsersIcon";
import { detailHref } from "../lib/catalog-context";
import { onSpaLinkClick } from "../lib/link-click";
import styles from "./PluginCard.module.css";

const SOURCE_LABELS: Record<string, string> = {
  ghec: "GHEC",
  ghes: "GHES",
};

const EXTENSION_TYPE_CLASS: Record<string, string> = {
  plugin: "typeBadgePlugin",
  skill: "typeBadgeSkill",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
interface PluginCardProps {
  plugin: MarketplaceEntry;
  onClick?: () => void;
  onClientClick?: (client: SupportedClient) => void;
  onCategoryClick?: (category: string) => void;
}

export default function PluginCard({ plugin, onClick, onClientClick, onCategoryClick }: PluginCardProps) {
  const cardClass = [styles.card, onClick ? styles.clickable : ""].filter(Boolean).join(" ");
  const updatedDate = plugin.lastUpdatedAt ? formatDate(plugin.lastUpdatedAt) : "";
  // Truthy check rather than `!== undefined`: a zero must never render as "used by 0",
  // because Contract 7 defines exactly ONE empty state and absence is the only signal
  // it can produce. fetchAdoption already rejects 0, so this is defence in depth.
  const activeUsers = plugin.adoption?.activeUsers30d;

  return (
    <article className={cardClass}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <OfficialCheck plugin={plugin} />
          <h3 className={styles.title}>
            {onClick ? (
              <a
                href={detailHref(plugin)}
                className={styles.cardLink}
                onClick={onSpaLinkClick(onClick)}
              >
                {plugin.pluginName}
              </a>
            ) : (
              plugin.pluginName
            )}
          </h3>
        </div>
        {plugin.extensionType && (
          <span className={`${styles.typeBadge} ${styles[EXTENSION_TYPE_CLASS[plugin.extensionType] ?? "typeBadgePlugin"]}`}>
            {plugin.extensionType}
          </span>
        )}
        {/* AFTER the type badge, not before: .typeBadge carries `margin-left: auto`, so
            it absorbs the free space in this row. A New badge placed before it would be
            shoved to the left edge beside the title instead of sitting next to it. */}
        <NewBadge plugin={plugin} />
      </div>

      <div className={styles.repoInfo}>
        <div>{plugin.orgOrOwner}/{plugin.repoName}</div>
        <div>{SOURCE_LABELS[plugin.source] ?? plugin.source}</div>
      </div>

      {/*
        ONE signal row carrying BOTH signals, separated by a middot, exactly as the
        prototype renders them: {adoptionInline} · {usefulnessInline}. It sits between
        the repo meta and the description, not in a footer — prominent on purpose,
        since this is the signal the epic exists to surface.

        #553 (adoption, merged) and this branch each added their own row here, and each
        left a note asking that they be folded into one when the branches met. This is
        that fold. Merged naively they stack vertically, and the CSS is the subtler
        half: two `.signalRow` rules both applied, so #571's tighter `gap: 0.35rem`
        silently won over #553's `0.9rem` — too tight once a middot separates two
        signals. The duplicate rule is removed rather than left to cascade.

        Adoption's empty state: the prototype renders nothing, but #266 AC 2 requires
        "a clear 'no usage yet' state rather than blank or broken", so the AC wins.

        Usefulness renders ALWAYS, including for an entry with no votes: #620's
        criterion is "fewer than 5 total votes" and zero is fewer than five. A card
        showing nothing would be indistinguishable from one whose signal failed to load.
      */}
      <div className={styles.signalRow}>
        {activeUsers ? (
          <span className={styles.signal}>
            <button type="button" className={styles.signalLabel}>
              <UsersIcon className={styles.signalIcon} />
              used by {activeUsers} (30d)
              <span className={styles.signalTooltip} aria-hidden="true">
                Distinct users who used this in the last 30 days (Claude Enterprise Analytics)
              </span>
            </button>
            <SharedUsageNote sharedNameUsage={plugin.adoption?.sharedNameUsage} extensionType={plugin.extensionType} />
          </span>
        ) : (
          <span className={styles.emptyPill}>no usage yet</span>
        )}
        {/* Decorative: the row already separates the two signals visually, and both
            carry their own text, so a screen reader gains nothing from reading "middot". */}
        <span className={styles.signalSeparator} aria-hidden="true">·</span>
        <UsefulnessBadge tally={plugin.feedback} />
      </div>

      <p className={styles.description}>
        {plugin.description || "No description available"}
      </p>

      <div className={styles.metadata}>
        <div>
          <div className={styles.sectionLabel}>Category</div>
          <div className={styles.pillList}>
            {plugin.categories && plugin.categories.length > 0 ? (
              plugin.categories.map((cat: string) => (
                <button
                  key={cat}
                  className={`${styles.pill} ${onCategoryClick ? styles.interactive : ""}`}
                  onClick={onCategoryClick ? () => onCategoryClick(cat) : undefined}
                  type="button"
                >
                  {cat}
                </button>
              ))
            ) : (
              <span className={styles.emptyPill}>Uncategorized</span>
            )}
          </div>
        </div>

        <div>
          <div className={styles.sectionLabel}>Works with</div>
          <div className={styles.pillList}>
            {plugin.supportedClients && plugin.supportedClients.length > 0 ? (
              plugin.supportedClients.map((client) => {
                const cfg = CLIENT_CONFIG[client] ?? { label: client, color: "var(--color-text-secondary)", bg: "var(--color-surface-secondary)" };
                return (
                  <button
                    key={client}
                    className={`${styles.pill} ${onClientClick ? styles.interactive : ""}`}
                    onClick={onClientClick ? () => onClientClick(client) : undefined}
                    type="button"
                  >
                    {cfg.label}
                  </button>
                );
              })
            ) : (
              <span className={styles.emptyPill}>Unknown</span>
            )}
          </div>
        </div>
      </div>

      {updatedDate && (
        <div className={styles.updatedDate}>
          Updated {updatedDate}
        </div>
      )}
    </article>
  );
}
